/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption, Stack } from '@fluentui/react';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { ResourceAssignmentDto } from 'src/core/services/resourceAssignmentSwaggers';
import { Region } from 'src/core/types/regionTypes';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';
import styled from 'styled-components';

type AddResourceAssignmentModalProps = BasicModalProps<object> & {
    isEditAssignment?: boolean;
    selectedRegionId?: string;
    selectedResourceId?: string;
    selectedSubscriptionId?: string;
    regionOptions: IDropdownOption[];
};

const messages = defineMessages({
    addAssignmentModalTitle: {
        id: 'modelDeploy.addResourceAssignmentModal.addAssignmentModalTitle',
        defaultMessage: 'Assign a resource to a deployment region'
    },
    editAssignmentModalTitle: {
        id: 'modelDeploy.addResourceAssignmentModal.editAssignmentModalTitle',
        defaultMessage: 'Edit an assignment to a deployment region'
    },
    modalSubTitle: {
        id: 'modelDeploy.addResourceAssignmentModal.modalSubTitle',
        defaultMessage: 'Make a region available when deploying by assigning a resource from that region.'
    },
    subscriptionDropDownLabel: {
        id: 'modelDeploy.addResourceAssignmentModal.subscriptionDropDownLabel',
        defaultMessage: 'Subscription'
    },
    subscriptionDropDownPlaceHolder: {
        id: 'modelDeploy.addResourceAssignmentModal.subscriptionDropDownPlaceHolder',
        defaultMessage: 'Select Azure subscription'
    },
    regionDropDownLabel: {
        id: 'modelDeploy.addResourceAssignmentModal.regionDropDownLabel',
        defaultMessage: 'Region'
    },
    regionDropDownPlaceHolder: {
        id: 'modelDeploy.addResourceAssignmentModal.regionDropDownPlaceHolder',
        defaultMessage: 'Select Region'
    },
    resourceDropDownLabel: {
        id: 'modelDeploy.addResourceAssignmentModal.resourceDropDownLabel',
        defaultMessage: 'Resource'
    },
    resourceDropDownPlaceHolder: {
        id: 'modelDeploy.addResourceAssignmentModal.resourceDropDownPlaceHolder',
        defaultMessage: 'Select Language resource'
    },
    assignButton: {
        id: 'modelDeploy.addResourceAssignmentModal.assignButton',
        defaultMessage: 'Assign'
    }
});

const ModalSubTitle = styled(LuisText)({ marginBottom: 20, display: 'block' });

export const AddResourceAssignmentModal = observer((props: AddResourceAssignmentModalProps) => {
    const {
        hidden,
        disabled,
        onDismiss,
        onSubmit,
        regionOptions,
        selectedRegionId,
        selectedResourceId,
        selectedSubscriptionId,
        isEditAssignment = false
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const [modalDropDownStates, updateModalDropDowns] = useUpdateState({
        selectedRegionId,
        selectedResourceId,
        selectedSubscriptionId: selectedSubscriptionId ?? rootStore.azureStore.session.subscriptionId
    });

    React.useEffect(() => {
        updateModalDropDowns({
            selectedRegionId,
            selectedResourceId,
            selectedSubscriptionId: selectedSubscriptionId ?? rootStore.azureStore.session.subscriptionId
        });
    }, [selectedRegionId, selectedResourceId, selectedSubscriptionId]);

    const { subscriptions, resourcesTracker, resourcesStoreHouse, subscriptionTracker } = useAzureResourcesManagement({
        subscriptionId: modalDropDownStates.selectedSubscriptionId
    });

    const subscriptionOptions: IDropdownOption[] = React.useMemo(() => subscriptions.map(item => ({ key: item.id, text: item.name })), [
        subscriptions
    ]);

    const resourceOptions: IDropdownOption[] = React.useMemo(
        () =>
            resourcesStoreHouse.items
                .filter(
                    item =>
                        item.kind === 'TextAnalytics' && item.region === modalDropDownStates.selectedRegionId && Boolean(item.customDomain)
                )
                .map(item => ({ key: item.path, text: item.name })),
        [resourcesStoreHouse.items, modalDropDownStates.selectedRegionId]
    );

    const isModalValid = React.useMemo(
        () =>
            Boolean(modalDropDownStates.selectedRegionId) &&
            Boolean(modalDropDownStates.selectedResourceId) &&
            Boolean(modalDropDownStates.selectedSubscriptionId),
        [modalDropDownStates.selectedRegionId, modalDropDownStates.selectedResourceId, modalDropDownStates.selectedSubscriptionId]
    );

    const getSelectedData = (): ResourceAssignmentDto => ({
        id: modalDropDownStates.selectedRegionId as Region,
        azureResourceId: modalDropDownStates.selectedResourceId,
        subscriptionId: modalDropDownStates.selectedSubscriptionId,
        regionName: regionOptions.find(item => item.key === modalDropDownStates.selectedRegionId)?.text,
        resourceName: resourceOptions.find(item => item.key === modalDropDownStates.selectedResourceId)?.text,
        subscriptionName: subscriptionOptions.find(item => item.key === modalDropDownStates.selectedSubscriptionId)?.text,
        customDomain: resourcesStoreHouse.items.find(resource => resource.path === modalDropDownStates.selectedResourceId)?.customDomain
    });

    const handleSubmit = () => {
        onSubmit(getSelectedData());
        updateModalDropDowns({
            selectedResourceId: isEditAssignment ? selectedResourceId : null,
            selectedRegionId: isEditAssignment ? selectedRegionId : null,
            selectedSubscriptionId: isEditAssignment ? selectedSubscriptionId : rootStore.azureStore.session.subscriptionId
        });
    };

    const handleDismiss = () => {
        updateModalDropDowns({
            selectedResourceId: isEditAssignment ? selectedResourceId : null,
            selectedRegionId: isEditAssignment ? selectedRegionId : null,
            selectedSubscriptionId: isEditAssignment ? selectedSubscriptionId : rootStore.azureStore.session.subscriptionId
        });

        onDismiss();
    };

    return (
        <BasicModal
            hidden={hidden}
            disabled={disabled}
            valid={isModalValid}
            onSubmit={handleSubmit}
            onDismiss={handleDismiss}
            doneButtonText={localize(messages.assignButton)}
            dialogContentProps={{
                title: localize(isEditAssignment ? messages.editAssignmentModalTitle : messages.addAssignmentModalTitle)
            }}>
            <ModalSubTitle>{localize(messages.modalSubTitle)}</ModalSubTitle>

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisDropdown
                    required
                    options={subscriptionOptions}
                    styles={{ root: { width: '300px' } }}
                    isLoading={subscriptionTracker.isRunning}
                    data-automation-id="subscriptionsDropdown"
                    label={localize(messages.subscriptionDropDownLabel)}
                    selectedKey={modalDropDownStates.selectedSubscriptionId}
                    placeholder={localize(messages.subscriptionDropDownPlaceHolder)}
                    onChange={(_, opt) => updateModalDropDowns({ selectedSubscriptionId: opt.key as string, selectedResourceId: null })}
                />

                <LuisDropdown
                    required
                    options={regionOptions}
                    disabled={isEditAssignment}
                    data-automation-id="regionsDropdown"
                    styles={{ root: { width: '300px' } }}
                    label={localize(messages.regionDropDownLabel)}
                    selectedKey={modalDropDownStates.selectedRegionId}
                    placeholder={localize(messages.regionDropDownPlaceHolder)}
                    onChange={(_, opt) => updateModalDropDowns({ selectedRegionId: opt.key as string, selectedResourceId: null })}
                />

                <LuisDropdown
                    required
                    options={resourceOptions}
                    styles={{ root: { width: '300px' } }}
                    isLoading={resourcesTracker.isRunning}
                    data-automation-id="resourcesDropdown"
                    label={localize(messages.resourceDropDownLabel)}
                    selectedKey={modalDropDownStates.selectedResourceId}
                    placeholder={localize(messages.resourceDropDownPlaceHolder)}
                    onChange={(_, opt) => updateModalDropDowns({ selectedResourceId: opt.key as string })}
                />
            </Stack>
        </BasicModal>
    );
});
